'use client';

import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

interface ColorEntry {
  id: string;
  hex: string;
  label: string;
  role: string;
}

const initialColors: ColorEntry[] = [
  { id: 'col-001', hex: '#1C1C1C', label: 'Schwarz', role: 'Primär' },
  { id: 'col-002', hex: '#FF5F1F', label: 'Neon-Orange', role: 'Akzent' },
  { id: 'col-003', hex: '#FFFFFF', label: 'Weiß', role: 'Text' },
  { id: 'col-004', hex: '#2A2A2A', label: 'Dunkelgrau', role: 'Hintergrund' },
  { id: 'col-005', hex: '#888888', label: 'Mittelgrau', role: 'Sekundär' },
];

export default function BrandVaultColors() {
  const [colors, setColors] = useState<ColorEntry[]>(initialColors);
  const [newHex, setNewHex] = useState('#');
  const [newLabel, setNewLabel] = useState('');

  const addColor = () => {
    if (newHex.match(/^#[0-9A-Fa-f]{6}$/) && newLabel.trim()) {
      const newId = `col-${String(colors.length + 1).padStart(3, '0')}`;
      setColors((prev) => [...prev, { id: newId, hex: newHex, label: newLabel.trim(), role: 'Benutzerdefiniert' }]);
      setNewHex('#');
      setNewLabel('');
      toast.success('Farbe hinzugefügt');
    } else {
      toast.error('Ungültiger Hex-Code oder fehlender Name');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addColor();
    }
  };

  const removeColor = (id: string) => {
    setColors((prev) => prev.filter((c) => c.id !== id));
  };

  return (
    <div className="punk-card p-5">
      <h2 className="text-sm font-700 text-white uppercase tracking-widest mb-4 pb-3 border-b border-border" id="colors-heading">
        Farbpalette
      </h2>

      <div className="space-y-2 mb-4" role="list" aria-labelledby="colors-heading">
        {colors.map((color) => (
          <div key={color.id} className="flex items-center gap-3 group" role="listitem">
            <div
              className="w-8 h-8 rounded border border-border flex-shrink-0"
              style={{ background: color.hex }}
              role="img"
              aria-label={`Farbvorschau: ${color.label} (${color.hex})`}
            />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-500 text-white">{color.label}</p>
              <p className="text-xs mono text-muted">{color.hex} · {color.role}</p>
            </div>
            <button
              onClick={() => removeColor(color.id)}
              className="opacity-0 group-hover:opacity-100 text-muted hover:text-red-400 transition-all p-1 focus:opacity-100 focus:outline-none focus:ring-1 focus:ring-red-400 rounded"
              aria-label={`Farbe "${color.label}" entfernen`}
            >
              <Trash2 size={11} aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>

      {/* Palette preview strip */}
      <div
        className="flex rounded overflow-hidden mb-4 h-6"
        role="img"
        aria-label={`Farbpalette Vorschau: ${colors.map((c) => c.label).join(', ')}`}
      >
        {colors.map((color) => (
          <div
            key={`strip-${color.id}`}
            className="flex-1"
            style={{ background: color.hex }}
            title={color.label}
          />
        ))}
      </div>

      {/* Add color */}
      <fieldset>
        <legend className="sr-only">Neue Farbe hinzufügen</legend>
        <div className="flex gap-2">
          <input
            type="text"
            className="input-punk w-24 font-mono text-xs"
            placeholder="#FF5F1F"
            value={newHex}
            onChange={(e) => setNewHex(e.target.value)}
            onKeyDown={handleKeyDown}
            maxLength={7}
            aria-label="Hex-Farbcode (z.B. #FF5F1F)"
          />
          <input
            type="text"
            className="input-punk flex-1 text-xs"
            placeholder="Farbname"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label="Farbname"
          />
          <button
            onClick={addColor}
            className="btn-ghost px-3 focus:outline-none focus:ring-1 focus:ring-accent rounded"
            aria-label="Farbe zur Palette hinzufügen"
            type="button"
          >
            <Plus size={14} aria-hidden="true" />
          </button>
        </div>
      </fieldset>
    </div>
  );
}